import React from "react";
import { cn } from "utils/cn";

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
	className?: string;
	variant?: "text" | "circular" | "rectangular";
	width?: string | number;
	height?: string | number;
	animation?: "pulse" | "wave" | "none";
}

const Skeleton: React.FC<SkeletonProps> = ({
	className,
	variant = "rectangular",
	width,
	height,
	animation = "pulse",
	...props
}) => {
	const variantClasses = {
		text: "h-4 rounded",
		circular: "rounded-full",
		rectangular: "rounded-md",
	};

	const animationClasses = {
		pulse: "animate-pulse",
		wave: "animate-shimmer",
		none: "",
	};

	const styles: React.CSSProperties = {};
	if (width) styles.width = typeof width === "number" ? `${width}px` : width;
	if (height)
		styles.height = typeof height === "number" ? `${height}px` : height;

	return (
		<div
			className={cn(
				"bg-muted",
				variantClasses[variant],
				animationClasses[animation],
				className,
			)}
			style={styles}
			{...props}
		/>
	);
};

// Preset skeleton components for common use cases
export const SkeletonCard: React.FC<{ className?: string }> = ({
	className,
}) => (
	<div className={cn("bg-card border border-border rounded-lg p-4", className)}>
		<Skeleton className="h-40 w-full mb-4" />
		<Skeleton className="h-4 w-3/4 mb-2" />
		<Skeleton className="h-4 w-1/2" />
	</div>
);

export const SkeletonTable: React.FC<{
	rows?: number;
	columns?: number;
	className?: string;
}> = ({ rows = 5, columns = 4, className }) => (
	<div className={cn("bg-card border border-border rounded-lg", className)}>
		{/* Header */}
		<div className="border-b border-border p-4">
			<div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
				{Array.from({ length: columns }).map((_, i) => (
					<Skeleton key={`header-${i}`} className="h-4" />
				))}
			</div>
		</div>

		{/* Rows */}
		{Array.from({ length: rows }).map((_, rowIndex) => (
			<div key={`row-${rowIndex}`} className="border-b border-border last:border-b-0 p-4">
				<div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
					{Array.from({ length: columns }).map((_, colIndex) => (
						<Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-4" />
					))}
				</div>
			</div>
		))}
	</div>
);

export const SkeletonStats: React.FC<{ count?: number; className?: string }> = ({
	count = 4,
	className,
}) => (
	<div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6", className)}>
		{Array.from({ length: count }).map((_, i) => (
			<div key={i} className="bg-card border border-border rounded-lg p-4 sm:p-6">
				<div className="flex items-center justify-between mb-4">
					<Skeleton className="h-10 w-10" variant="circular" />
					<Skeleton className="h-4 w-12" />
				</div>
				<Skeleton className="h-8 w-24 mb-2" />
				<Skeleton className="h-3 w-32" />
			</div>
		))}
	</div>
);

export const SkeletonAvatar: React.FC<{
	size?: number;
	className?: string;
}> = ({ size = 40, className }) => (
	<Skeleton
		variant="circular"
		width={size}
		height={size}
		className={className}
	/>
);

export const SkeletonText: React.FC<{
	lines?: number;
	className?: string;
}> = ({ lines = 3, className }) => (
	<div className={cn("space-y-2", className)}>
		{Array.from({ length: lines }).map((_, i) => (
			<Skeleton
				key={i}
				variant="text"
				className={cn(
					"h-4",
					i === lines - 1 && "w-3/4", // Last line is shorter
				)}
			/>
		))}
	</div>
);

export default Skeleton;
